import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Drawer as AntDrawer } from "antd";
import { useMatch, useRoutes } from "react-router";
import { Place } from "../../../modules/place/Place";
import { Tour } from "../../../modules/tour/Tour";
import { Event } from "../../../modules/event/Event";
import { Goal } from "../../../modules/goal/Goal";
import { FeedPost } from "../../../modules/feedPost/FeedPost";
import { useAppSelector } from "@store/store";
import { selectIsHeaderVisible } from "@store/app/appReducer";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";

interface DrawerProps {}

const routes = [
  {
    path: "/places/:idOrSlug",
    element: <Place />,
  },
  {
    path: "/tours/:idOrSlug",
    element: <Tour />,
  },
  {
    path: "/events/:idOrSlug",
    element: <Event />,
  },
  {
    path: "/goals/:idOrSlug",
    element: <Goal />,
  },
  {
    path: "/challenges/:idOrSlug",
    element: <Tour />,
  },
  {
    path: "/feed_posts/:idOrSlug",
    element: <FeedPost />,
  },
  // catch all route to avoid annoying warning when route doesn't match
  {
    path: "*",
    element: null,
  },
];

/**
 * A drawer that opens from the left side of the page. Currently only supports a Visit Widget
 * flyout.
 */
const Drawer: FunctionComponent<DrawerProps> = () => {
  const element = useRoutes(routes);
  const isPlanScreen = useMatch("/plan");
  const key = (element?.props as any)?.match?.pathname;
  const { isMobile } = useWindowSize();
  const path = (element?.props as any)?.match?.route?.path;
  const isMatched = path !== "*" && !isMobile;

  const [shownElement, setShownElement] = useState<React.ReactElement<
    any,
    string | React.JSXElementConstructor<any>
  > | null>(null);

  useEffect(() => {
    if (!element || !isMatched) {
      return;
    }

    setShownElement(element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const handleAfterOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setShownElement(null);
    }
  }, []);

  const isHeaderVisible = useAppSelector(selectIsHeaderVisible);

  return (
    <AntDrawer
      open={isMatched}
      width={isMobile ? "100%" : 372}
      placement={"left"}
      afterOpenChange={handleAfterOpenChange}
      mask={false}
      closable={false}
      style={{
        background: "none",
        pointerEvents: "none",
      }}
      rootStyle={{
        zIndex: 400,
        top: isMobile
          ? 0
          : !isHeaderVisible
            ? "12px"
            : isPlanScreen
              ? "72px"
              : "120px",
        bottom: isMobile ? 0 : "12px",
        borderRadius: isMobile ? 0 : "14px",
        pointerEvents: "none",
        outline: "none",
      }}
      styles={{
        body: {
          overflow: "hidden",
          padding: 0,
          display: "flex",
          justifyContent: "flex-end",
        },
        wrapper: {
          boxShadow: "none",
        },
      }}>
      <div
        style={{
          width: `${isMobile ? "100%" : "360px"}`,
          height: "100%",
          pointerEvents: "none",
          fontFamily: "'open sans', helvetica, arial, sans-serif",
          borderRadius: isMobile ? 0 : "14px",
          overflow: "hidden",
          ...(isMobile && {
            backgroundColor: "white",
          }),
        }}>
        <div
          style={{
            pointerEvents: "auto",
          }}>
          {shownElement}
        </div>
      </div>
    </AntDrawer>
  );
};

export { Drawer, DrawerProps };
